/** Turbo toggle button using layered sprite assets */

import { Container, Sprite, Texture, Assets, Graphics } from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('TurboButton');

const TURBO_ASSETS = {
  offBg: 'assets/ui/turbo_button_off_normal.png',
  offIcon: 'assets/sprites/turbo_icon_off_normal_en.png',
  offBgPressed: 'assets/ui/turbo_button_off_pressed.png',
  onBg: 'assets/ui/turbo_button_on_normal.png',
  onIcon: 'assets/sprites/turbo_icon_on_normal_en.png',
  onBgPressed: 'assets/ui/turbo_button_on_pressed.png',
};

/** Turbo mode toggle with layered background + icon sprites */
export class TurboButton extends Container {
  private _isOn: boolean = false;
  private _onChange: ((isOn: boolean) => void) | null = null;

  // Textures
  private _texOffBg: Texture | null = null;
  private _texOffIcon: Texture | null = null;
  private _texOnBg: Texture | null = null;
  private _texOnIcon: Texture | null = null;
  private _texOffBgPressed: Texture | null = null;
  private _texOnBgPressed: Texture | null = null;

  // Sprites (two layers)
  private _bgSprite: Sprite | null = null;
  private _iconSprite: Sprite | null = null;

  // Fallback
  private _fallbackBg: Graphics | null = null;
  private _useFallback: boolean = true;

  constructor() {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this._createFallback();
    this._loadAssets();

    this.on('pointerdown', this._handleDown, this);
    this.on('pointerup', this._handleUp, this);
    this.on('pointerupoutside', this._handleUp, this);
  }

  get isOn(): boolean {
    return this._isOn;
  }

  onChange(cb: (isOn: boolean) => void): void {
    this._onChange = cb;
  }

  private _createFallback(): void {
    const bg = new Graphics();
    const size = 60;
    bg.roundRect(-size / 2, -size / 2, size, size, 8);
    bg.fill({ color: 0x333355 });
    bg.stroke({ color: 0xccaa44, width: 2 });
    this._fallbackBg = bg;
    this.addChild(bg);
  }

  private async _loadAssets(): Promise<void> {
    try {
      const [offBg, offIcon, onBg, onIcon, offBgP, onBgP] = await Promise.all([
        Assets.load<Texture>(TURBO_ASSETS.offBg).catch(() => null),
        Assets.load<Texture>(TURBO_ASSETS.offIcon).catch(() => null),
        Assets.load<Texture>(TURBO_ASSETS.onBg).catch(() => null),
        Assets.load<Texture>(TURBO_ASSETS.onIcon).catch(() => null),
        Assets.load<Texture>(TURBO_ASSETS.offBgPressed).catch(() => null),
        Assets.load<Texture>(TURBO_ASSETS.onBgPressed).catch(() => null),
      ]);

      if (offBg) {
        this._texOffBg = offBg;
        this._texOffIcon = offIcon;
        this._texOnBg = onBg;
        this._texOnIcon = onIcon;
        this._texOffBgPressed = offBgP;
        this._texOnBgPressed = onBgP;

        this._bgSprite = new Sprite(offBg);
        this._bgSprite.anchor.set(0.5);
        this.addChild(this._bgSprite);

        this._iconSprite = new Sprite(offIcon || offBg);
        this._iconSprite.anchor.set(0.5);
        this.addChild(this._iconSprite);

        // Remove fallback
        if (this._fallbackBg) {
          this.removeChild(this._fallbackBg);
          this._fallbackBg.destroy();
          this._fallbackBg = null;
        }
        this._useFallback = false;

        logger.info('Turbo button assets loaded');
        this._updateVisuals();
      } else {
        logger.warn('Turbo button assets not found, using fallback');
      }
    } catch (err) {
      logger.warn(`Failed to load turbo assets: ${err}`);
    }
  }

  private _updateVisuals(): void {
    if (this._useFallback) {
      this._updateFallback();
      return;
    }

    if (this._bgSprite) {
      this._bgSprite.texture = this._isOn
        ? (this._texOnBg || this._texOffBg!)
        : this._texOffBg!;
    }
    if (this._iconSprite) {
      if (this._isOn && this._texOnIcon) {
        this._iconSprite.texture = this._texOnIcon;
        this._iconSprite.visible = true;
      } else if (!this._isOn && this._texOffIcon) {
        this._iconSprite.texture = this._texOffIcon;
        this._iconSprite.visible = true;
      } else {
        this._iconSprite.visible = false;
      }
    }
  }

  private _updateFallback(): void {
    if (!this._fallbackBg) return;
    const size = 60;
    this._fallbackBg.clear();
    this._fallbackBg.roundRect(-size / 2, -size / 2, size, size, 8);
    this._fallbackBg.fill({ color: this._isOn ? 0x884400 : 0x333355 });
    this._fallbackBg.stroke({ color: this._isOn ? 0xffaa33 : 0xccaa44, width: 2 });
  }

  private _handleDown(): void {
    // Toggle state
    this._isOn = !this._isOn;

    // Show pressed texture
    if (!this._useFallback && this._bgSprite) {
      if (this._isOn && this._texOnBgPressed) {
        this._bgSprite.texture = this._texOnBgPressed;
      } else if (!this._isOn && this._texOffBgPressed) {
        this._bgSprite.texture = this._texOffBgPressed;
      }
    }

    if (this._onChange) {
      this._onChange(this._isOn);
    }
    logger.info(`Turbo toggled: ${this._isOn}`);
  }

  private _handleUp(): void {
    this._updateVisuals();
  }
}
